import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  PersonOff as NotAvailableIcon,
  MusicNote as RehearsalIcon,
  TheaterComedy as PerformancePlanIcon,
  Stars as PerformanceIcon,
  Refresh as RefreshIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import UserManagement from './components/UserManagement';
import FullCalendar from '@fullcalendar/react';
import { EventInput, EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import listPlugin from '@fullcalendar/list';
import deLocale from '@fullcalendar/core/locales/de';
import './App.css';

// Person definitions are now loaded dynamically from the backend

// Event type definitions
const EVENT_TYPES = {
  nicht_da: {
    label: 'Nicht da',
    icon: NotAvailableIcon,
    pattern: 'diagonal-lines',
    opacity: 0.6,
    isIndividual: true
  },
  probentermin: {
    label: 'Probentermin',
    icon: RehearsalIcon,
    pattern: 'event-pattern-diagonal',
    opacity: 0.8,
    isIndividual: false
  },
  auffuehrung_plan: {
    label: 'Aufführung Plan',
    icon: PerformancePlanIcon,
    pattern: 'event-pattern-horizontal',
    opacity: 0.9,
    isIndividual: false
  },
  auffuehrung: {
    label: 'Aufführung',
    icon: PerformanceIcon,
    pattern: 'event-pattern-crosses',
    opacity: 1.0,
    isIndividual: false
  }
};

interface CustomEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  startTime?: string; // Time in HH:mm format (only used when allDay is false)
  endTime?: string; // Time in HH:mm format (only used when allDay is false)
  person?: string; // Person ID, optional for group events
  eventType: keyof typeof EVENT_TYPES;
  comment?: string;
  location?: string;
}

interface User {
  id: string;
  username: string;
  name: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  color?: string;
  createdAt: string;
  lastLogin?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
}

function App() {

  const [events, setEvents] = useState<EventInput[]>([]);
  const [visibleYears, setVisibleYears] = useState<Set<number>>(new Set([2025]));
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Custom events state
  const [customEvents, setCustomEvents] = useState<CustomEvent[]>([]);

  // Dynamic persons state - loaded from backend
  const [persons, setPersons] = useState<Record<string, { name: string; color: string }>>({});
  const [personsLoading, setPersonsLoading] = useState(true);

  // Calendar ref to access calendar API
  const calendarRef = useRef<any>(null);
  
  // Ref to track the last saved events to prevent duplicate saves
  const lastSavedEventsRef = useRef<string>('');

  // Current view state to filter events
  const [currentView, setCurrentView] = useState('multiMonthGrid');
  const [filteredEvents, setFilteredEvents] = useState<EventInput[]>([]);

  // Holiday cache state - cache holidays by year to reduce API calls
  const [holidayCache, setHolidayCache] = useState<Map<number, EventInput[]>>(new Map());

  // Authentication state
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false
  });



  // Auth loading state
  const [authLoading, setAuthLoading] = useState(true);
  
  // Flag to prevent multiple simultaneous auth checks
  const [authCheckInProgress, setAuthCheckInProgress] = useState(false);

  // Auth modal state
  const [authModal, setAuthModal] = useState<{
    open: boolean;
  }>({ open: false });

  // Auth form state
  const [authForm, setAuthForm] = useState({
    username: '',
    password: ''
  });

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // User management state
  const [userManagementOpen, setUserManagementOpen] = useState(false);

  // URL-based user identification
  const [urlUser, setUrlUser] = useState<User | null>(null);
  const [isUrlBasedAccess, setIsUrlBasedAccess] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dateStr: string;
  } | null>(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 480);

  // Event modification dialog state
  const [eventModifyDialog, setEventModifyDialog] = useState<{
    open: boolean;
    event: CustomEvent | null;
  }>({ open: false, event: null });

  // Modal state
  const [eventModal, setEventModal] = useState<{
    open: boolean;
    dateStr: string;
    person?: string;
    eventType?: keyof typeof EVENT_TYPES;
    mode: 'create' | 'edit';
    editingEvent?: CustomEvent;
  }>({ open: false, dateStr: '', mode: 'create' });

  // Form state
  const [formData, setFormData] = useState({
    person: '' as string,
    eventType: '' as keyof typeof EVENT_TYPES | '',
    comment: '',
    location: '',
    startDate: '',
    endDate: '',
    allDay: true, // Default to "Ganzer Tag"
    startTime: '19:00', // Default start time (Probenbeginn)
    endTime: '22:00' // Default end time
  });

  // Backend API configuration
  const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001/api';

  // URL-based user functions
  const generateUserUrl = (userId: string) => {
    return `${window.location.origin}/user/${userId}`;
  };

  const copyUserUrl = async (userId: string) => {
    const url = generateUserUrl(userId);
    try {
      await navigator.clipboard.writeText(url);
      setNotification({
        open: true,
        message: 'URL in Zwischenablage kopiert!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Failed to copy URL:', error);
      setNotification({
        open: true,
        message: 'Fehler beim Kopieren der URL',
        severity: 'error'
      });
    }
  };

  const checkUrlBasedAccess = useCallback(async () => {
    const path = window.location.pathname;
    const userMatch = path.match(/^\/user\/([a-zA-Z0-9\-_]+)$/);
    
    if (userMatch) {
      const userId = userMatch[1];
      
      try {
        // Validate the user ID against the backend
        const response = await fetch(`${API_BASE_URL}/public/users/${userId}`);
        
        if (!response.ok) {
          setAuthLoading(false);
          return false;
        }
        
        const userData = await response.json();
        
        const validUser: User = {
          id: userData.id,
          username: userData.username,
          name: userData.name,
          email: userData.email,
          role: 'viewer', // URL users are always viewers
          createdAt: userData.createdAt,
          lastLogin: new Date().toISOString()
        };
        
        setUrlUser(validUser);
        setIsUrlBasedAccess(true);
        
        // Set auth state for URL-based access
        setAuth({
          user: validUser,
          token: null, // No token for URL-based access
          isAuthenticated: true
        });
        
        // Ensure authLoading is set to false for URL-based access
        setAuthLoading(false);
        
        return true;
      } catch (error) {
        console.error('Error validating user ID:', error);
        setAuthLoading(false);
        return false;
      }
    }
    
    // If not a URL-based access, clear URL-based state only if currently set
    setUrlUser(null);
    setIsUrlBasedAccess(false);
    
    // Don't set authLoading here - let the normal auth flow handle it
    return false;
  }, [API_BASE_URL]); // Add API_BASE_URL as dependency

  // Authentication functions
  const login = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Anmeldung fehlgeschlagen');
      }

      const data = await response.json();
      const authState: AuthState = {
        user: data.user,
        token: data.token,
        isAuthenticated: true
      };

      setAuth(authState);
      localStorage.setItem('token', data.token);
      setAuthModal({ open: false });
      setNotification({
        open: true,
        message: `Willkommen, ${data.user.name}!`,
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen',
        severity: 'error'
      });
    }
  };

  const logout = () => {
    setAuth({ user: null, token: null, isAuthenticated: false });
    localStorage.removeItem('token');
    // Clear cache and events on logout
    setHolidayCache(new Map());
    setEvents([]);
    setCustomEvents([]);
    // Close any open dialogs
    setUserManagementOpen(false);
    setAuthModal({ open: false });
    setEventModal({ open: false, dateStr: '', mode: 'create' });
    setContextMenu(null);
    
    // Clear URL-based access
    setUrlUser(null);
    setIsUrlBasedAccess(false);
    
    // Redirect to home page if using URL-based access
    if (isUrlBasedAccess) {
      window.location.href = '/';
    }
    
    setNotification({
      open: true,
      message: 'Sie wurden abgemeldet.',
      severity: 'info'
    });
  };

  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (authCheckInProgress) {
      return;
    }
    
    setAuthCheckInProgress(true);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      setAuth({ user: null, token: null, isAuthenticated: false });
      setAuthLoading(false);
      setAuthCheckInProgress(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const user = await response.json();
        setAuth({
          user,
          token,
          isAuthenticated: true
        });
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('token');
        setAuth({ user: null, token: null, isAuthenticated: false });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setAuth({ user: null, token: null, isAuthenticated: false });
    } finally {
      setAuthLoading(false);
      setAuthCheckInProgress(false);
    }
  }, [API_BASE_URL]);

  // Check authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      // First check for URL-based access immediately
      if (await checkUrlBasedAccess()) {
        return;
      }
      
      // Then check for token-based authentication
      await checkAuth();
    };
    initAuth();
  }, []); // Only run once on mount

  // Monitor URL changes and re-authenticate if needed
  useEffect(() => {
    const checkCurrentUrl = async () => {
      const currentPath = window.location.pathname;
      
      // If we're on a user URL but not authenticated via URL, try again
      if (currentPath.match(/^\/user\/([a-zA-Z0-9]+)$/) && !isUrlBasedAccess) {
        await checkUrlBasedAccess();
      }
    };

    // Check immediately
    checkCurrentUrl();

    // Set up an interval to periodically check URL (as fallback)
    const interval = setInterval(checkCurrentUrl, 1000);

    return () => clearInterval(interval);
  }, [isUrlBasedAccess, checkUrlBasedAccess]);

  // Listen for URL changes to handle URL-based access
  useEffect(() => {
    const handleUrlChange = async () => {
      // Check for URL-based access when URL changes
      await checkUrlBasedAccess();
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []); // Only set up the listener once

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallMobile(window.innerWidth < 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to add auth headers
  const getAuthHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (auth.token) {
      headers['Authorization'] = `Bearer ${auth.token}`;
    }
    
    return headers;
  };

  // Load active users/persons from backend
  const loadActivePersons = useCallback(async (): Promise<void> => {
    if (!auth.isAuthenticated) {
      console.log('🔒 Skipping persons load - not authenticated');
      return;
    }

    console.log(`📂 Loading active persons from backend...`);
    
    try {
      // Use public endpoint for URL-based users, authenticated endpoint for others
      let endpoint: string;
      let headers: Record<string, string>;
      
      if (isUrlBasedAccess) {
        endpoint = 'public/users/active';
        headers = { 'Content-Type': 'application/json' };
      } else {
        endpoint = 'users/active';
        headers = getAuthHeaders();
      }
      
      console.log(`🔍 Using ${isUrlBasedAccess ? 'public' : 'authenticated'} persons endpoint: ${endpoint}`);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        headers
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          if (!isUrlBasedAccess) {
            logout();
          }
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const activeUsers = await response.json();
      console.log(`✅ Loaded ${activeUsers.length} active persons from backend`);
      
      // Convert to persons format for compatibility
      const personsMap: Record<string, { name: string; color: string }> = {};
      activeUsers.forEach((user: any) => {
        personsMap[user.id] = {
          name: user.name,
          color: user.color || '#1976d2' // fallback color
        };
      });
      
      setPersons(personsMap);
      setPersonsLoading(false);
    } catch (err) {
      console.error(`❌ Error loading persons from backend:`, err);
      setPersonsLoading(false);
    }
  }, [auth.isAuthenticated, isUrlBasedAccess, API_BASE_URL]);

  const fetchHolidaysFromBackend = useCallback(async (years: Set<number>): Promise<EventInput[]> => {
    // Don't fetch if not authenticated or still loading
    if (!auth.isAuthenticated || authLoading) {
      console.log('🔒 Skipping holiday fetch - not authenticated or still loading');
      return [];
    }

    const yearsArray = Array.from(years);
    console.log(`🔍 Fetching holidays from backend for years: ${yearsArray.join(', ')}`);

    // Check cache first for each year
    const cachedHolidays: EventInput[] = [];
    const yearsToFetch: number[] = [];

    for (const year of yearsArray) {
      if (holidayCache.has(year)) {
        console.log(`📋 Using cached holidays for year ${year}`);
        cachedHolidays.push(...holidayCache.get(year)!);
      } else {
        yearsToFetch.push(year);
      }
    }

    // If all years are cached, return cached data
    if (yearsToFetch.length === 0) {
      console.log(`✅ All holidays loaded from cache for years: ${yearsArray.join(', ')}`);
      return cachedHolidays;
    }

    // Fetch only the years that aren't cached
    console.log(`🔍 Fetching holidays from backend for uncached years: ${yearsToFetch.join(', ')}`);

    try {
      // Use public endpoint for URL-based users (no token), authenticated endpoint for others
      const endpoint = auth.token ? 'holidays' : 'public/holidays';
      console.log(`🔍 Using ${auth.token ? 'authenticated' : 'public'} holiday endpoint: ${endpoint}`);
      const response = await fetch(`${API_BASE_URL}/${endpoint}/${yearsToFetch.join(',')}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, redirect to login (but not for URL-based users)
          if (!isUrlBasedAccess) {
            logout();
          }
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const newHolidays = await response.json();
      console.log(`✅ Received ${newHolidays.length} holidays from backend for years: ${yearsToFetch.join(', ')}`);

      // Cache the new holidays by year
      const newCache = new Map(holidayCache);
      for (const year of yearsToFetch) {
        const yearHolidays = newHolidays.filter((holiday: EventInput) => {
          const holidayYear = new Date(holiday.start as string).getFullYear();
          return holidayYear === year;
        });
        newCache.set(year, yearHolidays);
        console.log(`💾 Cached ${yearHolidays.length} holidays for year ${year}`);
      }
      setHolidayCache(newCache);

      // Combine cached and new holidays
      const allHolidays = [...cachedHolidays, ...newHolidays];
      console.log(`✅ Total holidays loaded: ${allHolidays.length} (${cachedHolidays.length} from cache, ${newHolidays.length} from API)`);
      
      return allHolidays;
    } catch (err) {
      console.error(`❌ Error fetching holidays from backend:`, err);
      return cachedHolidays; // Return cached data even if new fetch fails
    }
  }, [holidayCache, auth.token, API_BASE_URL, auth.isAuthenticated]);

  // Function to clear holiday cache (useful for debugging or force refresh)
  const clearHolidayCache = useCallback(() => {
    console.log('🗑️ Clearing holiday cache');
    setHolidayCache(new Map());
  }, []);

  // Function to get cache statistics
  const getCacheStats = useCallback(() => {
    const cachedYears = Array.from(holidayCache.keys());
    const totalCachedHolidays = Array.from(holidayCache.values()).reduce((sum, holidays) => sum + holidays.length, 0);
    console.log(`📊 Cache stats: ${cachedYears.length} years cached, ${totalCachedHolidays} total holidays`);
    return { cachedYears, totalCachedHolidays };
  }, [holidayCache]);

  // Load calendar events from backend
  const loadCalendarFromBackend = useCallback(async (): Promise<CustomEvent[]> => {
    // Don't fetch if not authenticated or still loading
    if (!auth.isAuthenticated || authLoading) {
      console.log('🔒 Skipping calendar load - not authenticated or still loading');
      return [];
    }

    console.log(`📂 Loading calendar from backend...`);
    
    try {
      // Use public endpoint for URL-based users, authenticated endpoint for others
      let endpoint: string;
      let headers: Record<string, string>;
      
      if (isUrlBasedAccess && urlUser) {
        endpoint = `public/calendar/${urlUser.id}`;
        headers = { 'Content-Type': 'application/json' };
      } else if (isUrlBasedAccess && !urlUser) {
        // This shouldn't happen, but handle gracefully
        console.error('🚨 URL-based access detected but no urlUser available');
        throw new Error('Invalid user session. Please refresh the page.');
      } else {
        endpoint = 'calendar';
        headers = getAuthHeaders();
      }
      
      console.log(`🔍 Using ${isUrlBasedAccess ? 'public' : 'authenticated'} calendar endpoint: ${endpoint}`);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        headers
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Don't logout URL-based users - they don't have tokens
          if (!isUrlBasedAccess) {
            logout();
          }
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const calendar = await response.json();
      console.log(`✅ Loaded ${calendar.events?.length || 0} events from backend`);
      return calendar.events || [];
    } catch (err) {
      console.error(`❌ Error loading calendar from backend:`, err);
      return [];
    }
  }, [auth.token, API_BASE_URL, auth.isAuthenticated, isUrlBasedAccess, urlUser]);

  // Save calendar events to backend
  const saveCalendarToBackend = useCallback(async (events: CustomEvent[]): Promise<void> => {
    // Don't save if not authenticated or still loading
    if (!auth.isAuthenticated || authLoading) {
      console.log('🔒 Skipping calendar save - not authenticated or still loading');
      return;
    }

    console.log(`💾 Saving ${events.length} events to backend...`);
    
    try {
      // Use public endpoint for URL-based users, authenticated endpoint for others
      let endpoint: string;
      let headers: Record<string, string>;
      
      if (isUrlBasedAccess && urlUser) {
        endpoint = `public/calendar/${urlUser.id}`;
        headers = { 'Content-Type': 'application/json' };
      } else if (isUrlBasedAccess && !urlUser) {
        // This shouldn't happen, but handle gracefully
        console.error('🚨 URL-based access detected but no urlUser available');
        throw new Error('Invalid user session. Please refresh the page.');
      } else {
        endpoint = 'calendar';
        headers = getAuthHeaders();
      }
      
      console.log(`🔍 Using ${isUrlBasedAccess ? 'public' : 'authenticated'} calendar save endpoint: ${endpoint}`);
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ events }),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Don't logout URL-based users - they don't have tokens
          if (!isUrlBasedAccess) {
            logout();
          }
          throw new Error('Authentication required');
        }
        if (response.status === 403) {
          throw new Error('Insufficient permissions to save events');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`✅ Calendar saved successfully:`, result.message);
    } catch (err) {
      console.error(`❌ Error saving calendar to backend:`, err);
      setNotification({
        open: true,
        message: err instanceof Error ? err.message : 'Kalender konnte nicht gespeichert werden',
        severity: 'error'
      });
    }
  }, [auth.token, API_BASE_URL, auth.isAuthenticated, isUrlBasedAccess, urlUser]);

  const convertCustomEventsToFullCalendar = useCallback((customEvs: CustomEvent[]): EventInput[] => {
    return customEvs.map(event => {
      const eventType = EVENT_TYPES[event.eventType];
      
      let title: string;
      let color: string;
      
      if (event.person) {
        // Individual event
        const person = persons[event.person];
        if (person) {
          title = `${person.name}: ${eventType.label}`;
          color = person.color;
        } else {
          // Fallback if person not found
          title = `Unknown: ${eventType.label}`;
          color = '#757575'; // Gray for unknown person
        }
      } else {
        // Group event
        title = eventType.label;
        color = '#1976d2'; // Blue for group events
      }
      
      // Add location and comment differently for different event types
      if (event.eventType === 'auffuehrung' || event.eventType === 'auffuehrung_plan') {
        // For performances, add location on new line
        if (event.location) title += `\n@ ${event.location}`;
        if (event.comment) title += ` (${event.comment})`;
      } else {
        // For other events, add inline
        if (event.location) title += ` @ ${event.location}`;
        if (event.comment) title += ` (${event.comment})`;
      }

      return {
        id: event.id,
        title,
        start: event.start,
        end: event.end,
        allDay: event.allDay,
        color,
        extendedProps: {
          type: 'custom',
          person: event.person,
          eventType: event.eventType,
          comment: event.comment,
          location: event.location,
          opacity: eventType.opacity
        }
      };
    });
  }, [persons]);

  // Update filtered events when view or events change
  useEffect(() => {
    if (currentView === 'listCustom' || currentView === 'listYear') {
      // For list view, only show group events (exclude individual "nicht_da" events and holidays)
      const groupEvents = customEvents.filter(event => 
        event.eventType === 'probentermin' || 
        event.eventType === 'auffuehrung_plan' || 
        event.eventType === 'auffuehrung'
      );
      setFilteredEvents(convertCustomEventsToFullCalendar(groupEvents));
    } else {
      // For all other views, show all events (holidays + custom)
      setFilteredEvents(events);
    }
  }, [currentView, events, customEvents, convertCustomEventsToFullCalendar]);

  const fetchHolidaysForAllVisibleYears = useCallback(async (years: Set<number>) => {
    setLoading(true);
    setError(null);

    console.log(`🔍 Fetching holidays for years: ${Array.from(years).join(', ')}`);

    try {
      const holidayEvents = await fetchHolidaysFromBackend(years);
      const customEventsConverted = convertCustomEventsToFullCalendar(customEvents);
      const allEvents = [...holidayEvents, ...customEventsConverted];

      console.log(`✅ Total events loaded: ${allEvents.length} (${holidayEvents.length} holidays, ${customEventsConverted.length} custom)`);

      if (allEvents.length === 0) {
        setError(`Keine Feriendaten für die Jahre ${Array.from(years).join(', ')} verfügbar.`);
      } else {
        setError(null);
      }

      setEvents(allEvents);
    } catch (err) {
      console.error('❌ Error fetching holidays:', err);
      setError(`Fehler beim Laden der Feriendaten. Bitte versuchen Sie es später erneut.`);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [fetchHolidaysFromBackend, customEvents, convertCustomEventsToFullCalendar]);

  // Load active persons when authentication changes
  useEffect(() => {
    if (auth.isAuthenticated) {
      loadActivePersons();
    } else {
      // Clear persons when not authenticated
      setPersons({});
      setPersonsLoading(true);
    }
  }, [loadActivePersons, auth.isAuthenticated]);

  // Load calendar events from backend on mount
  useEffect(() => {
    if (auth.isAuthenticated) {
      const loadInitialData = async () => {
        const backendEvents = await loadCalendarFromBackend();
        setCustomEvents(backendEvents);
      };
      loadInitialData();
    } else {
      // Clear events when not authenticated
      setCustomEvents([]);
      setEvents([]);
    }
  }, [loadCalendarFromBackend, auth.isAuthenticated]);

  // Fetch holidays when visible years change
  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchHolidaysForAllVisibleYears(visibleYears);
    } else {
      // Clear events when not authenticated
      setEvents([]);
    }
  }, [visibleYears, fetchHolidaysForAllVisibleYears, auth.isAuthenticated]);

  // Save calendar events to backend when they change (with duplicate save prevention)
  useEffect(() => {
    if (auth.isAuthenticated && customEvents.length > 0) {
      // Create a hash of the events to check if they actually changed
      const eventsHash = JSON.stringify(customEvents.map(e => ({ id: e.id, start: e.start, end: e.end, eventType: e.eventType, person: e.person })));
      
      if (eventsHash !== lastSavedEventsRef.current) {
        console.log('📝 Events changed, saving to backend...');
        lastSavedEventsRef.current = eventsHash;
        saveCalendarToBackend(customEvents);
      } else {
        console.log('📝 Events unchanged, skipping save');
      }
    }
  }, [customEvents, saveCalendarToBackend, auth.isAuthenticated]);



  const handleDatesSet = (arg: any) => {
    // Get all years that are visible in the current view
    const startYear = arg.start.getFullYear();
    const endYear = arg.end.getFullYear();

    const newVisibleYears = new Set<number>();
    for (let year = startYear; year <= endYear; year++) {
      newVisibleYears.add(year);
    }

    console.log(`📅 Calendar showing dates from ${arg.start.toISOString().split('T')[0]} to ${arg.end.toISOString().split('T')[0]}`);
    console.log(`📅 Years in view: ${Array.from(newVisibleYears).join(', ')}`);

    // Only fetch if the visible years changed
    const currentYearsArray = Array.from(visibleYears).sort();
    const newYearsArray = Array.from(newVisibleYears).sort();

    if (JSON.stringify(currentYearsArray) !== JSON.stringify(newYearsArray)) {
      console.log(`🔄 Visible years changed from [${currentYearsArray.join(', ')}] to [${newYearsArray.join(', ')}]`);
      setVisibleYears(newVisibleYears);
    }
  };

  const handleDateClick = (arg: any) => {
    if (!auth.isAuthenticated) {
      setNotification({
        open: true,
        message: 'Bitte melden Sie sich an, um Termine zu erstellen.',
        severity: 'info'
      });
      return;
    }
    
    console.log('Date clicked:', arg.dateStr);
    setEventModal({ open: true, dateStr: arg.dateStr, mode: 'create' });
    setFormData({ person: '', eventType: '', comment: '', location: '', startDate: arg.dateStr, endDate: arg.dateStr, allDay: true, startTime: '19:00', endTime: '22:00' });
  };

  const handleContextMenu = (info: any) => {
    if (!auth.isAuthenticated) {
      setNotification({
        open: true,
        message: 'Bitte melden Sie sich an, um Termine zu erstellen.',
        severity: 'info'
      });
      return;
    }
    
    info.jsEvent.preventDefault();
    setContextMenu({
      mouseX: info.jsEvent.clientX + 2,
      mouseY: info.jsEvent.clientY - 6,
      dateStr: info.dateStr
    });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleQuickAdd = (personId: string | undefined, eventType: keyof typeof EVENT_TYPES) => {
    setEventModal({ open: true, dateStr: contextMenu?.dateStr || '', mode: 'create' });
    setFormData({ 
      person: personId || '', 
      eventType, 
      comment: '', 
      location: '', 
      startDate: contextMenu?.dateStr || '', 
      endDate: contextMenu?.dateStr || '',
      allDay: true,
      startTime: '19:00',
      endTime: '22:00'
    });
    setContextMenu(null);
  };

  const handleCreateEvent = () => {
    if (!formData.eventType || !formData.startDate) {
      setNotification({
        open: true,
        message: 'Bitte füllen Sie alle erforderlichen Felder aus.',
        severity: 'error'
      });
      return;
    }

    // Validate that end date is not before start date
    if (formData.endDate < formData.startDate) {
      setNotification({
        open: true,
        message: 'Das Enddatum darf nicht vor dem Startdatum liegen.',
        severity: 'error'
      });
      return;
    }

    // Build start and end datetime strings
    let startDateTime = formData.startDate;
    let endDateTime = formData.endDate || formData.startDate;
    
    if (!formData.allDay) {
      // Add time to date strings for non-all-day events
      startDateTime += `T${formData.startTime}:00`;
      endDateTime += `T${formData.endTime}:00`;
    } else if (formData.endDate && formData.endDate !== formData.startDate) {
      // For all-day multi-day events, convert user's inclusive end date to exclusive for storage
      // User enters "until September 21" → we store "until September 22" (exclusive)
      const [year, month, day] = formData.endDate.split('-').map(Number);
      const nextDay = new Date(Date.UTC(year, month - 1, day + 1)); // Add 1 day directly
      endDateTime = nextDay.toISOString().split('T')[0];
    }

    if (eventModal.mode === 'edit' && eventModal.editingEvent) {
      // Update existing event
      const updatedEvent: CustomEvent = {
        ...eventModal.editingEvent,
        start: startDateTime,
        end: endDateTime,
        allDay: formData.allDay,
        startTime: formData.allDay ? undefined : formData.startTime,
        endTime: formData.allDay ? undefined : formData.endTime,
        person: formData.person || undefined,
        eventType: formData.eventType,
        comment: formData.comment || undefined,
        location: formData.location || undefined
      };

      setCustomEvents(prev => prev.map(e => e.id === eventModal.editingEvent!.id ? updatedEvent : e));
      
      const dateRangeText = formData.startDate === formData.endDate 
        ? `am ${new Date(formData.startDate + 'T00:00').toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          })}`
        : `vom ${new Date(formData.startDate + 'T00:00').toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit' 
          })} bis ${new Date(formData.endDate + 'T00:00').toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          })} (einschließlich)`;
      
      setNotification({
        open: true,
        message: `Termin ${dateRangeText} erfolgreich aktualisiert!`,
        severity: 'success'
      });
    } else {
      // Create new event
      const newEvent: CustomEvent = {
        id: Date.now().toString(),
        title: '',
        start: startDateTime,
        end: endDateTime,
        allDay: formData.allDay,
        startTime: formData.allDay ? undefined : formData.startTime,
        endTime: formData.allDay ? undefined : formData.endTime,
        person: formData.person || undefined,
        eventType: formData.eventType,
        comment: formData.comment || undefined,
        location: formData.location || undefined
      };

      setCustomEvents(prev => [...prev, newEvent]);
      
      const dateRangeText = formData.startDate === formData.endDate 
        ? `am ${new Date(formData.startDate + 'T00:00').toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          })}`
        : `vom ${new Date(formData.startDate + 'T00:00').toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit' 
          })} bis ${new Date(formData.endDate + 'T00:00').toLocaleDateString('de-DE', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          })} (einschließlich)`;
      
      setNotification({
        open: true,
        message: `Termin ${dateRangeText} erfolgreich erstellt!`,
        severity: 'success'
      });
    }

    setEventModal({ open: false, dateStr: '', mode: 'create' });
    setFormData({ person: '', eventType: '', comment: '', location: '', startDate: '', endDate: '', allDay: true, startTime: '19:00', endTime: '22:00' });
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    if (clickInfo.event.extendedProps.type === 'custom') {
      // Find the custom event
      const customEvent = customEvents.find(e => e.id === clickInfo.event.id);
      if (customEvent) {
        setEventModifyDialog({ open: true, event: customEvent });
      }
    }
  };

  const handleEventModify = (event: CustomEvent) => {
    // Open the event creation modal in edit mode
    setEventModal({ 
      open: true, 
      dateStr: event.start.split('T')[0], 
      mode: 'edit',
      editingEvent: event
    });
    
    // Pre-fill the form with event data
    // For all-day multi-day events, convert exclusive storage back to inclusive for form display
    let formEndDate = event.end ? event.end.split('T')[0] : event.start.split('T')[0];
    if (event.allDay && event.end && event.end !== event.start) {
      // Convert exclusive end date back to inclusive for user-friendly form display
      const [year, month, day] = event.end.split('-').map(Number);
      const prevDay = new Date(Date.UTC(year, month - 1, day - 1)); // Subtract 1 day directly
      formEndDate = prevDay.toISOString().split('T')[0];
    }
    
    setFormData({
      person: event.person || '',
      eventType: event.eventType,
      comment: event.comment || '',
      location: event.location || '',
      startDate: event.start.split('T')[0],
      endDate: formEndDate,
      allDay: event.allDay,
      startTime: event.startTime || '19:00',
      endTime: event.endTime || '22:00'
    });
    
    setEventModifyDialog({ open: false, event: null });
  };

  const handleEventDelete = (event: CustomEvent) => {
    if (window.confirm(`Termin "${event.title || EVENT_TYPES[event.eventType].label}" wirklich löschen?`)) {
      setCustomEvents(prev => prev.filter(e => e.id !== event.id));
      setEventModifyDialog({ open: false, event: null });
      
      setNotification({
        open: true,
        message: 'Termin erfolgreich gelöscht!',
        severity: 'success'
      });
    }
  };

  const needsComment = formData.eventType === 'probentermin';
  const needsLocation = formData.eventType === 'auffuehrung_plan' || formData.eventType === 'auffuehrung';

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        bgcolor: '#f5f5f5', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Container maxWidth="sm">
          <Box sx={{ 
            bgcolor: 'white', 
            p: 4, 
            borderRadius: 2, 
            boxShadow: 3,
            textAlign: 'center'
          }}>
            <Typography variant="h5" component="h1" gutterBottom>
              Lade Terminplaner...
            </Typography>
            <Box sx={{ mt: 2 }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }} />
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  // If not authenticated, show login screen
  if (!auth.isAuthenticated) {
    return (
      <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Box sx={{ 
            bgcolor: 'white', 
            p: 4, 
            borderRadius: 2, 
            boxShadow: 3,
            textAlign: 'center'
          }}>
            <Typography variant="h4" component="h1" gutterBottom>
              HTS Terminplaner
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => setAuthModal({ open: true })}
                size="large"
              >
                Admin Anmelden
              </Button>
            </Stack>
          </Box>
        </Container>

        {/* Auth Modal */}
        <Dialog open={authModal.open} onClose={() => setAuthModal({ open: false })} maxWidth="sm" fullWidth>
          <DialogTitle>
            Admin Anmelden
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Benutzername"
                value={authForm.username}
                onChange={(e) => setAuthForm(prev => ({ ...prev, username: e.target.value }))}
              />
              
              <TextField
                fullWidth
                label="Passwort"
                type="password"
                value={authForm.password}
                onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAuthModal({ open: false })}>
              Abbrechen
            </Button>
            <Button
              onClick={() => login(authForm.username, authForm.password)}
              variant="contained"
              disabled={!authForm.username || !authForm.password}
            >
              Anmelden
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          <Alert 
            onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
            severity={notification.severity}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  console.log('🔓 Authenticated, showing main app. Auth state:', auth);
  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar sx={{ 
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 1 },
          py: { xs: 0.5, sm: 1 }
        }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              minWidth: { xs: '120px', sm: 'auto' }
            }}
          >
            HTS Terminplaner
          </Typography>
          <Stack 
            direction="row" 
            spacing={{ xs: 0.5, sm: 1 }} 
            alignItems="center"
            sx={{
              flexWrap: 'wrap',
              gap: { xs: 0.25, sm: 0.5 }
            }}
          >
            {!personsLoading && Object.entries(persons).map(([id, person]) => (
              <Chip
                key={id}
                label={person.name}
                sx={{
                  bgcolor: person.color,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  height: { xs: '24px', sm: '32px' }
                }}
                size="small"
              />
            ))}
            {/* Cache status indicator */}
            <Chip
              label={`Cache: ${holidayCache.size} Jahre`}
              size="small"
              sx={{
                bgcolor: '#4caf50',
                color: 'white',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                height: { xs: '24px', sm: '32px' }
              }}
              onClick={() => {
                const stats = getCacheStats();
                console.log('Cache Statistics:', stats);
                alert(`Cache Status:\n${stats.cachedYears.length} Jahre im Cache\n${stats.totalCachedHolidays} Ferientage gespeichert\n\nGecachte Jahre: ${stats.cachedYears.join(', ')}`);
              }}
              title="Klick für Cache-Statistiken"
            />
            {/* Cache clear button */}
            <Tooltip title="Cache leeren (alle Feriendaten neu laden)">
              <IconButton
                size="small"
                onClick={() => {
                  if (window.confirm('Cache leeren und alle Feriendaten neu laden?')) {
                    clearHolidayCache();
                    // Force refetch of current visible years
                    fetchHolidaysForAllVisibleYears(visibleYears);
                  }
                }}
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* User management button (admin only) */}
            {auth.user?.role === 'admin' && (
              <Tooltip title="Benutzerverwaltung">
                <IconButton
                  size="small"
                  onClick={() => setUserManagementOpen(true)}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <PeopleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Debug info - show current auth state 
            {process.env.NODE_ENV === 'development' && (
              <Chip
                                  label={`Debug: ${auth.isAuthenticated ? 'Angemeldet' : 'Nicht angemeldet'} - Rolle: ${auth.user?.role || 'keine'}`}
                size="small"
                sx={{
                  bgcolor: '#ff9800',
                  color: 'white',
                  fontSize: '0.7rem'
                }}
              />
            )}
            */}
            {/* User info */}
            <Chip
              label={auth.user?.name || auth.user?.username || 'User'}
              icon={auth.user?.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
              size="small"
              sx={{
                bgcolor: auth.user?.role === 'admin' ? '#f57c00' : '#1976d2',
                color: 'white',
                fontWeight: 'bold',
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                height: { xs: '24px', sm: '32px' }
              }}
            />
            {/* URL-based access indicator */}
            {isUrlBasedAccess && (
              <Chip
                label="URL-Zugang"
                size="small"
                sx={{
                  bgcolor: '#4caf50',
                  color: 'white',
                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                  height: { xs: '20px', sm: '24px' }
                }}
              />
            )}
            
            {/* ICS Calendar URL button - Only for URL-based access */}
            {isUrlBasedAccess && urlUser && (
              <Tooltip title="Kalender-URL für externe Kalender (Google Calendar, Outlook, etc.)">
                <IconButton
                  size="small"
                                  onClick={() => {
                  const icsUrl = `${API_BASE_URL}/public/calendar/${urlUser.id}/ics`;
                    
                    // Copy URL to clipboard
                    navigator.clipboard.writeText(icsUrl).then(() => {
                      setNotification({
                        open: true,
                        message: 'Kalender-URL in Zwischenablage kopiert! Fügen Sie diese URL in Ihren Kalender ein.',
                        severity: 'success'
                      });
                    }).catch(() => {
                      // Fallback: show URL in alert
                      alert(`Kalender-URL:\n\n${icsUrl}\n\nKopieren Sie diese URL und fügen Sie sie in Ihren Kalender ein.`);
                    });
                  }}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Logout button */}
            <Tooltip title="Abmelden">
              <IconButton
                size="small"
                onClick={logout}
                sx={{
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>
      <Container 
        maxWidth={false} 
        sx={{ 
          mt: { xs: 1, md: 2 }, 
          px: { xs: 1, sm: 2, md: 3 },
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {error && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 1 }}>
            <Typography color="text.secondary">
              {error}
            </Typography>
          </Box>
        )}
        {loading && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography color="text.secondary">
              Lade Feriendaten für {Array.from(visibleYears).join(', ')}...
            </Typography>
          </Box>
        )}
        

        
        <Box sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          bgcolor: 'white',
          boxShadow: 1,
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          height: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 140px)', md: 'calc(100vh - 160px)' },
          '& .fc': {
            height: '100% !important',
            width: '100% !important'
          },
          '& .fc-toolbar': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0',
            padding: { xs: '4px 8px', sm: '8px 12px', md: '12px 16px' },
            flexWrap: 'wrap',
            gap: { xs: 0.25, sm: 0.5, md: 1 },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            margin: 0,
            minHeight: 'auto',
            '& .fc-toolbar-chunk': {
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.25, sm: 0.5, md: 1 },
              flexWrap: 'wrap'
            },
            '& .fc-toolbar-title': {
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 'bold',
              color: '#1976d2',
              textAlign: { xs: 'center', sm: 'left' }
            },
            '& .fc-button': {
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '4px 8px', sm: '6px 12px' },
              minWidth: { xs: 'auto', sm: 'auto' }
            },
            '& .fc-button-group': {
              gap: { xs: '2px', sm: '4px' }
            }
          },
          '& .fc-view-harness': {
            height: 'calc(100% - 60px)',
            overflow: 'auto',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch'
          },
          '& .fc-view': {
            padding: '0',
            height: '100%'
          },
          '& .fc-multimonth': {
            gap: { xs: '4px', sm: '8px' },
            padding: { xs: '4px', sm: '8px' }
          },
          '& .fc-multimonth-month': {
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
            minWidth: { xs: '280px', sm: '300px', md: '350px' }
          },
          '& .fc-multimonth-title': {
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
            padding: { xs: '6px', sm: '8px' },
            textAlign: 'center',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .fc-day-today': {
            backgroundColor: '#e3f2fd !important',
            border: '2px solid #1976d2 !important',
            fontWeight: 'bold'
          },
          '& .fc-day-today .fc-daygrid-day-number': {
            color: '#1976d2 !important',
            fontWeight: 'bold'
          },
          '& .fc-daygrid-day': {
            minHeight: { xs: '60px', sm: '80px', md: '100px' }
          },
          '& .fc-daygrid-day-number': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .fc-daygrid-day-frame': {
            padding: { xs: '2px', sm: '4px' }
          },
          '& .fc-list': {
            borderColor: '#e0e0e0'
          },
          '& .fc-list-event': {
            cursor: 'pointer',
            borderBottom: '1px solid #f0f0f0',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          },
          '& .fc-list-day-cushion': {
            backgroundColor: '#1976d2',
            color: 'white',
            fontWeight: 'bold',
            padding: { xs: '6px 8px', sm: '8px 12px' },
            fontSize: { xs: '0.875rem', sm: '0.95rem' }
          },
          '& .fc-list-event-dot': {
            borderColor: 'currentColor',
            width: '8px',
            height: '8px'
          },
          '& .fc-list-event-title': {
            fontSize: { xs: '0.875rem', sm: '0.9rem' },
            fontWeight: '500'
          },
          '& .fc-list-event-time': {
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            color: '#666'
          },
          '& .fc-list-empty': {
            textAlign: 'center',
            padding: { xs: '20px 10px', sm: '40px 20px' },
            color: '#999',
            fontStyle: 'italic'
          }
        }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin, multiMonthPlugin, listPlugin]}
            initialView="multiMonthGrid"
            initialDate={new Date()}
            height="auto"
            events={filteredEvents}
            locale={deLocale}
            firstDay={1}
            fixedWeekCount={false}
            showNonCurrentDates={false}
            headerToolbar={{
              left: 'prev,next todayCustom',
              center: 'title',
              right: isMobile ? 'dayGridMonth,listCustom' : 'dayGridMonth,multiMonthGrid,multiMonthStack,listCustom'
            }}
            footerToolbar={false}
            customButtons={{
              todayCustom: {
                text: 'Heute',
                click: () => {
                  if (calendarRef.current) {
                    const calendarApi = calendarRef.current.getApi();
                    const today = new Date();
                    
                    // Always go to today's date, regardless of current view
                    calendarApi.gotoDate(today);
                    
                    // If in multi-month view, ensure today is visible and centered
                    setTimeout(() => {
                      const todayElement = document.querySelector('.fc-day-today');
                      if (todayElement) {
                        todayElement.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }
                    }, 100);
                  }
                }
              }
            }}
            buttonText={{
              month: 'Monat',
              multiMonthGrid: 'Übersicht',
              multiMonthStack: 'Fortlaufend',
              listCustom: 'Liste'
            }}
            views={{
              multiMonthGrid: {
                type: 'multiMonthYear',
                multiMonthMaxColumns: isMobile ? 1 : isSmallMobile ? 1 : 3,
                multiMonthMinWidth: isMobile ? 280 : isSmallMobile ? 280 : 350
              },
              multiMonthStack: {
                type: 'multiMonthYear',
                multiMonthMaxColumns: 1,
                multiMonthMinWidth: isMobile ? 280 : isSmallMobile ? 280 : 350
              },
              listCustom: {
                type: 'listYear',
                listDayFormat: { weekday: 'long', month: 'long', day: 'numeric' },
                listDaySideFormat: false,
                noEventsText: 'Keine Termine vorhanden',
                listDayAltFormat: false
              }
            }}
            viewDidMount={(info) => {
              // Update current view state
              setCurrentView(info.view.type);
              
              // Scroll to today's date on initial load in multi-month views
              if (info.view.type.includes('multiMonth')) {
                setTimeout(() => {
                  const todayElement = document.querySelector('.fc-day-today');
                  if (todayElement) {
                    todayElement.scrollIntoView({ 
                      behavior: 'smooth', 
                      block: 'center' 
                    });
                  }
                }, 200);
              }

              // Add event listener for right-click (only for grid views)
              if (!info.view.type.includes('list')) {
                const calendarEl = document.querySelector('.fc-daygrid-day');
                if (calendarEl) {
                  document.addEventListener('contextmenu', (e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('.fc-daygrid-day')) {
                      e.preventDefault();
                      // Find the date from the clicked cell
                      const dayEl = target.closest('.fc-daygrid-day') as HTMLElement;
                      if (dayEl && dayEl.dataset.date) {
                        handleContextMenu({
                          jsEvent: e,
                          dateStr: dayEl.dataset.date
                        });
                      }
                    }
                  });
                }
              }
            }}

            datesSet={(arg) => {
              handleDatesSet(arg);
              // Update current view when dates change (which happens on view change)
              if (calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                const currentViewType = calendarApi.view.type;
                if (currentViewType !== currentView) {
                  setCurrentView(currentViewType);
                }
              }
            }}
          eventWillUnmount={(info) => {
            // Clean up pattern classes when events are removed
            if (info.event.extendedProps.type === 'custom') {
              const eventType = info.event.extendedProps.eventType;
              if (eventType && !EVENT_TYPES[eventType as keyof typeof EVENT_TYPES]?.isIndividual) {
                const dayCell = info.el.closest('.fc-daygrid-day');
                if (dayCell) {
                  dayCell.classList.remove('has-probentermin', 'has-auffuehrung-plan', 'has-auffuehrung');
                }
              }
            }
          }}
          dateClick={(info) => {
            // Check if it's a right-click (contextmenu)
            if (info.jsEvent.button === 2) {
              handleContextMenu(info);
            } else {
              handleDateClick(info);
            }
          }}
          eventClick={handleEventClick}
          eventDidMount={(info) => {
            // Add data attribute to distinguish event types for CSS targeting
            info.el.setAttribute('data-event-type', info.event.extendedProps.type || 'holiday');
            
            if (info.event.extendedProps.opacity) {
              info.el.style.opacity = info.event.extendedProps.opacity;
            }
            
            // Apply pattern classes to the day cell for group events
            if (info.event.extendedProps.type === 'custom') {
              const eventType = info.event.extendedProps.eventType;
              if (eventType && !EVENT_TYPES[eventType as keyof typeof EVENT_TYPES]?.isIndividual) {
                // Find the parent day cell
                const dayCell = info.el.closest('.fc-daygrid-day');
                if (dayCell) {
                  // Add pattern class based on event type
                  switch (eventType) {
                    case 'probentermin':
                      dayCell.classList.add('has-probentermin');
                      break;
                    case 'auffuehrung_plan':
                      dayCell.classList.add('has-auffuehrung-plan');
                      break;
                    case 'auffuehrung':
                      dayCell.classList.add('has-auffuehrung');
                      break;
                  }
                }
              }
            }
          }}
        />
        </Box>
      </Container>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {[
          // Individual events (Nicht da) - one for each person
          ...(!personsLoading ? Object.entries(persons).map(([personId, person]) => (
            <MenuItem
              key={`${personId}-nicht_da`}
              onClick={() => handleQuickAdd(personId, 'nicht_da')}
            >
              <ListItemIcon>
                <NotAvailableIcon sx={{ color: person.color, fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText 
                primary={`${person.name}: Nicht da`}
                primaryTypographyProps={{ 
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              />
            </MenuItem>
          )) : []),
          
          // Divider
          <MenuItem key="divider-group" divider sx={{ padding: 0, minHeight: 1 }} />,
          
          // Group events - one for each type
          ...Object.entries(EVENT_TYPES)
            .filter(([_, eventType]) => !eventType.isIndividual)
            .map(([eventTypeKey, eventType]) => (
              <MenuItem
                key={`group-${eventTypeKey}`}
                onClick={() => handleQuickAdd(undefined, eventTypeKey as keyof typeof EVENT_TYPES)}
                sx={{ pl: 3 }}
              >
                <ListItemIcon>
                  <eventType.icon sx={{ color: '#1976d2', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText 
                  primary={eventType.label}
                  primaryTypographyProps={{ 
                    fontSize: '0.8125rem',
                    fontWeight: 400
                  }}
                />
              </MenuItem>
            ))
        ]}
      </Menu>

      {/* Event Creation Modal */}
              <Dialog open={eventModal.open} onClose={() => setEventModal({ open: false, dateStr: '', mode: 'create' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {eventModal.mode === 'edit' ? 'Termin bearbeiten' : `Neuer Termin für ${eventModal.dateStr}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Show person selection only for individual events */}
            {formData.eventType && EVENT_TYPES[formData.eventType]?.isIndividual && (
              <FormControl fullWidth>
                <InputLabel>Person</InputLabel>
                <Select
                  value={formData.person}
                  label="Person"
                  onChange={(e) => setFormData(prev => ({ ...prev, person: e.target.value as string }))}
                >
                  {Object.entries(persons).map(([id, person]) => (
                    <MenuItem key={id} value={id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: person.color
                          }}
                        />
                        {person.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth>
              <InputLabel>Typ</InputLabel>
              <Select
                value={formData.eventType}
                label="Typ"
                onChange={(e) => setFormData(prev => ({ ...prev, eventType: e.target.value as keyof typeof EVENT_TYPES }))}
              >
                {Object.entries(EVENT_TYPES).map(([key, eventType]) => {
                  const IconComponent = eventType.icon;
                  return (
                    <MenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconComponent fontSize="small" />
                        {eventType.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Startdatum"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  startDate: e.target.value,
                  // If end date is before start date, update it to start date
                  endDate: prev.endDate < e.target.value ? e.target.value : prev.endDate
                }))}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                label="Enddatum (inklusive)"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: formData.startDate, // Prevent end date before start date
                }}
                helperText={
                  formData.startDate === formData.endDate 
                    ? 'Eintägiger Termin' 
                    : `Termin läuft bis einschließlich ${new Date(formData.endDate + 'T00:00').toLocaleDateString('de-DE', { 
                        weekday: 'short', 
                        day: '2-digit', 
                        month: '2-digit' 
                      })}`
                }
              />
            </Box>

            {/* All-day toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.allDay}
                  onChange={(e) => setFormData(prev => ({ ...prev, allDay: e.target.checked }))}
                  color="primary"
                />
              }
              label="Ganzer Tag"
              sx={{ alignSelf: 'flex-start' }}
            />

            {/* Time fields - shown only when not all-day */}
            {!formData.allDay && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Startzeit"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 minute intervals
                    'data-format': '24hr'
                  }}
                  helperText="24-Stunden Format (HH:MM)"
                />
                <TextField
                  fullWidth
                  label="Endzeit"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formData.startTime, // Prevent end time before start time on same day
                    step: 300, // 5 minute intervals
                    'data-format': '24hr'
                  }}
                  helperText="24-Stunden Format (HH:MM)"
                />
              </Box>
            )}

            {needsComment && (
              <TextField
                fullWidth
                label="Kommentar"
                value={formData.comment}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="z.B. Szene 1-3, Kostümprobe..."
              />
            )}

            {needsLocation && (
              <TextField
                fullWidth
                label="Ort"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="z.B. Aula, Stadttheater..."
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventModal({ open: false, dateStr: '', mode: 'create' })}>
            Abbrechen
          </Button>
          <Button
            onClick={handleCreateEvent}
            variant="contained"
            disabled={
              !formData.eventType || 
              !formData.startDate || 
              (EVENT_TYPES[formData.eventType]?.isIndividual && !formData.person) ||
              (needsLocation && !formData.location)
            }
          >
            {eventModal.mode === 'edit' ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Modification Dialog */}
      <Dialog open={eventModifyDialog.open} onClose={() => setEventModifyDialog({ open: false, event: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Termin bearbeiten
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {eventModifyDialog.event && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {EVENT_TYPES[eventModifyDialog.event.eventType].label}
                </Typography>
                
                {eventModifyDialog.event.person && (
                  <Typography variant="body2" color="text.secondary">
                    Person: {persons[eventModifyDialog.event.person]?.name || 'Unbekannt'}
                  </Typography>
                )}
                
                <Typography variant="body2" color="text.secondary">
                  Datum: {(() => {
                    const startDate = new Date(eventModifyDialog.event.start).toLocaleDateString('de-DE');
                    if (eventModifyDialog.event.end && eventModifyDialog.event.end !== eventModifyDialog.event.start) {
                      // For multi-day events, convert exclusive end date back to inclusive for display
                      let displayEndDate;
                      if (eventModifyDialog.event.allDay) {
                        const [year, month, day] = eventModifyDialog.event.end.split('-').map(Number);
                        const prevDay = new Date(Date.UTC(year, month - 1, day - 1)); // Subtract 1 day directly
                        displayEndDate = prevDay.toLocaleDateString('de-DE');
                      } else {
                        displayEndDate = new Date(eventModifyDialog.event.end).toLocaleDateString('de-DE');
                      }
                      return `${startDate} bis ${displayEndDate} (einschließlich)`;
                    }
                    return startDate;
                  })()}
                </Typography>
                
                {eventModifyDialog.event.comment && (
                  <Typography variant="body2" color="text.secondary">
                    Kommentar: {eventModifyDialog.event.comment}
                  </Typography>
                )}
                
                {eventModifyDialog.event.location && (
                  <Typography variant="body2" color="text.secondary">
                    Ort: {eventModifyDialog.event.location}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventModifyDialog({ open: false, event: null })}>
            Abbrechen
          </Button>
          <Button
            onClick={() => eventModifyDialog.event && handleEventModify(eventModifyDialog.event)}
            variant="contained"
            color="primary"
          >
            Bearbeiten
          </Button>
          <Button
            onClick={() => eventModifyDialog.event && handleEventDelete(eventModifyDialog.event)}
            variant="contained"
            color="error"
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Management Dialog */}
      <UserManagement
        open={userManagementOpen}
        onClose={() => {
          // Close dialog
          setUserManagementOpen(false);
          // Reload persons so that changed user colors / roles are reflected immediately
          loadActivePersons();
          // Re-create event list so existing calendar events pick up the new colors
          fetchHolidaysForAllVisibleYears(visibleYears);
        }}
        token={auth.token || ''}
        apiBaseUrl={API_BASE_URL}
      />

      {/* Auth Modal */}
      <Dialog open={authModal.open} onClose={() => setAuthModal({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          Admin Anmelden
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Benutzername"
              value={authForm.username}
              onChange={(e) => setAuthForm(prev => ({ ...prev, username: e.target.value }))}
            />
            
            <TextField
              fullWidth
              label="Passwort"
              type="password"
              value={authForm.password}
              onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthModal({ open: false })}>
            Abbrechen
          </Button>
          <Button
            onClick={() => login(authForm.username, authForm.password)}
            variant="contained"
            disabled={!authForm.username || !authForm.password}
          >
            Anmelden
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
